import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { InvoiceResponse, PaymentType } from '../../../@types/invoice/invoice.type';
import axiosInstance from '../../../utils/axios';

// ---------- Types ----------
interface RequestState<T = any> {
    data: T | null;
    loading: boolean;
    error: string | null;
}

interface InvoiceState {
    list: RequestState<InvoiceResponse[]>;
    pay: RequestState<InvoiceResponse>;
}

// ---------- Initial State ----------
const initialRequestState = <T,>(): RequestState<T> => ({
    data: null,
    loading: false,
    error: null,
});

const initialState: InvoiceState = {
    list: initialRequestState<InvoiceResponse[]>(),
    pay: initialRequestState<InvoiceResponse>(),
};

// ---------- Thunks ----------
const BASE_URL = '/api/v1/invoices';

export const fetchInvoicesByOrgID = createAsyncThunk(
    'invoices/fetchByOrgID',
    async (orgId: string, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get(`${BASE_URL}/organization/${orgId}`);
            return res.data as InvoiceResponse[];
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || 'Fetch failed');
        }
    }
);

export const fetchInvoicesByParentOrgID = createAsyncThunk(
    'invoices/fetchByParentOrgID',
    async (parentOrgId: string, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get(`${BASE_URL}/parent-organization/${parentOrgId}`);
            return res.data as InvoiceResponse[];
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || 'Fetch failed');
        }
    }
);

export const fetchAllInvoices = createAsyncThunk(
    'invoices/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get(`${BASE_URL}`);
            return res.data as InvoiceResponse[];
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || 'Fetch failed');
        }
    }
);

export const payInvoice = createAsyncThunk(
    'invoices/pay',
    async (
        {
            id,
            proofReference,
            paymentType,
            file,
        }: { id: number; proofReference?: string; paymentType: PaymentType; file: File },
        { rejectWithValue }
    ) => {
        try {
            const formData = new FormData();
            if (proofReference) formData.append('proofReference', proofReference);
            formData.append('paymentType', paymentType);
            formData.append('file', file);
            const res = await axiosInstance.post(`${BASE_URL}/${id}/pay`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return res.data as InvoiceResponse;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || 'Payment failed');
        }
    }
);

// ---------- Slice ----------
const invoiceSlice = createSlice({
    name: 'invoices',
    initialState,
    reducers: {
        clearPayState: (state) => {
            state.pay = initialRequestState();
        },
    },
    extraReducers: (builder) => {
        // Fetch By Org ID
        builder.addCase(fetchInvoicesByOrgID.pending, (state) => {
            state.list.loading = true;
            state.list.error = null;
            state.list.data = [];
        });
        builder.addCase(fetchInvoicesByOrgID.fulfilled, (state, action) => {
            state.list.loading = false;
            state.list.data = action.payload;
        });
        builder.addCase(fetchInvoicesByOrgID.rejected, (state, action) => {
            state.list.loading = false;
            state.list.error = action.payload as string;
            state.list.data = [];
        });

        // Fetch All Invoices
        builder.addCase(fetchAllInvoices.pending, (state) => {
            state.list.loading = true;
            state.list.error = null;
        });
        builder.addCase(fetchAllInvoices.fulfilled, (state, action) => {
            state.list.loading = false;
            state.list.data = action.payload;
        });
        builder.addCase(fetchAllInvoices.rejected, (state, action) => {
            state.list.loading = false;
            state.list.error = action.payload as string;
        });

        // Fetch By Parent Org ID
        builder.addCase(fetchInvoicesByParentOrgID.pending, (state) => {
            state.list.loading = true;
            state.list.error = null;
            state.list.data = [];
        });
        builder.addCase(fetchInvoicesByParentOrgID.fulfilled, (state, action) => {
            state.list.loading = false;
            state.list.data = action.payload;
        });
        builder.addCase(fetchInvoicesByParentOrgID.rejected, (state, action) => {
            state.list.loading = false;
            state.list.error = action.payload as string;
            state.list.data = [];
        });

        // Pay
        builder.addCase(payInvoice.pending, (state) => {
            state.pay.loading = true;
            state.pay.error = null;
        });
        builder.addCase(payInvoice.fulfilled, (state, action) => {
            state.pay.loading = false;
            state.pay.data = action.payload;
            const updated = action.payload;
            if (state.list.data && updated) {
                state.list.data = state.list.data.map((invoice) =>
                    invoice.id === updated.id ? { ...invoice, ...updated } : invoice
                );
            }
        });
        builder.addCase(payInvoice.rejected, (state, action) => {
            state.pay.loading = false;
            state.pay.error = action.payload as string;
        });
    },
});

export const { clearPayState } = invoiceSlice.actions;
export default invoiceSlice.reducer; 